import { reactive, computed, ref, getCurrentInstance } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import {
  listPayRanges,
  payRangeLifecycleAction,
  savePayRangeDraft,
  uploadPayRangeSourceDocument,
  voidPayRangeVersion,
  publishPayRangeVersion,
  discardPayRangeVersion,
} from "@/api/payRangeAPI";
import {
  getChatterList,
  getOperatingCountry,
  getOperatingCurrency,
} from "@/api";
import {
  mapPayRangeListRow,
  mapUiStatusesToApi,
} from "@components/PayRangesListPage/mapPayRangeListRow";
import {
  toApiDateString,
  dayAfterApiDate,
  maxApiDate,
} from "@components/PayRangeDetailPage/composables/usePayRangeDetailPageState";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { FAIR_PAY_TAB, fairPayListRoute } from "@components/FairPayMenu/fairPayMenuConstants";
import {
  PAY_RANGE_CHATTER_TABLE,
  transformPayRangeChatterResults,
} from "@components/utils/payRangeChatter";
import {
  flagcdnFromCode,
  resolveCountryFlagIcon,
  resolveFlagUrl,
} from "@components/utils/countryFlagIcon";

const STATUS_OPTIONS = ["draft", "active", "retired", "archived"];
const STATUS_SET = new Set(STATUS_OPTIONS);
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const PICKER_PAGE_SIZE = 50;

const STATUS_CHIP = {
  draft: "Draft",
  active: "In Force",
  retired: "Retired",
  archived: "Archived",
};

const PAY_BASIS_OPTIONS = [
  { label: "Hourly", value: "HOURLY" },
  { label: "Monthly", value: "MONTHLY" },
  { label: "Annual", value: "ANNUAL" },
];

const WORK_MODE_OPTIONS = [
  { label: "On Site", value: "ON_SITE" },
  { label: "Hybrid", value: "HYBRID" },
  { label: "Remote", value: "REMOTE" },
];

const PAY_BASIS_VALUES = new Set(PAY_BASIS_OPTIONS.map((o) => o.value));
const WORK_MODE_VALUES = new Set(WORK_MODE_OPTIONS.map((o) => o.value));

const COUNTRY_PREFIX = "country:";
const CURRENCY_PREFIX = "currency:";

export {
  PAY_RANGE_CHATTER_TABLE,
  formatPayRangeChatterMessage,
  transformPayRangeChatterResults,
} from "@components/utils/payRangeChatter";

function capitalizeStatus(value) {
  const key = String(value || "").toLowerCase();
  return STATUS_CHIP[key] || value;
}

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

/** Survives tab remount so chips never flash bare country/currency ids. */
const PICKER_LABEL_STORAGE_KEY = "payRangeListPickerLabels";

function readStoredPickerLabels() {
  try {
    const raw = localStorage.getItem(PICKER_LABEL_STORAGE_KEY);
    if (!raw) return { countries: {}, currencies: {} };
    const parsed = JSON.parse(raw);
    return {
      countries:
        parsed?.countries && typeof parsed.countries === "object"
          ? parsed.countries
          : {},
      currencies:
        parsed?.currencies && typeof parsed.currencies === "object"
          ? parsed.currencies
          : {},
    };
  } catch {
    return { countries: {}, currencies: {} };
  }
}

function writeStoredPickerLabels(countriesMap, currenciesMap) {
  try {
    localStorage.setItem(
      PICKER_LABEL_STORAGE_KEY,
      JSON.stringify({
        countries: Object.fromEntries(countriesMap.entries()),
        currencies: Object.fromEntries(currenciesMap.entries()),
      })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function countryDisplayName(item) {
  if (!item || item.id == null) return "";
  return (
    item.name ||
    item.country?.name ||
    item.label ||
    item.code ||
    item.country?.code ||
    ""
  );
}

function currencyDisplayName(item) {
  if (!item || item.id == null) return "";
  const name =
    item.name || item.currency_name || item.label || item.currency?.name || "";
  const code =
    item.code || item.currency_code || item.currency?.code || "";
  if (name && code && name !== code) return `${name} (${code})`;
  return name || code || "";
}

function normalizeCountrySelection(item) {
  if (!item?.id) return null;
  const name = countryDisplayName(item);
  const code = item.code || item.country?.code || "";
  return {
    ...item,
    id: item.id,
    name: name || code || "",
    code,
    icon: item.icon || item.country?.flag_icon || item.country?.flag || "",
  };
}

function normalizeCurrencySelection(item) {
  if (!item?.id) return null;
  const name =
    item.name || item.currency_name || item.label || item.currency?.name || "";
  const code =
    item.code || item.currency_code || item.currency?.code || "";
  return {
    ...item,
    id: item.id,
    name: name || code || "",
    code,
    icon:
      item.icon ||
      (Array.isArray(item.flags) ? item.flags[0] : "") ||
      "",
  };
}

function sameFilterSet(a, b) {
  const left = [...(a || [])].map(String).sort();
  const right = [...(b || [])].map(String).sort();
  if (left.length !== right.length) return false;
  return left.every((v, i) => v === right[i]);
}

function debounce(fn, delayMs) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delayMs);
  };
}

function extractResults(response) {
  const data = response?.data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data)) return data;
  return [];
}

/** Map operating-country list row → FlagPickerField item. */
export function mapCountryPickerItem(row) {
  if (!row?.id) return null;
  const country = row.country || {};
  const code = String(country.code || row.code || "").trim();
  // Never fall back to raw id — that flashes in chips/pickers before hydrate.
  const name = country.name || row.name || code || "";
  const icon = resolveCountryFlagIcon({
    code,
    flag_icon: country.flag_icon || row.flag_icon,
    flag: country.flag || row.flag,
    icon: country.icon || row.icon,
  });
  return { id: row.id, name, code, icon };
}

/** Map operating-currency list row → FlagPickerField item. */
function mapCurrencyPickerItem(row) {
  if (!row?.id) return null;
  const code = row.currency_code || row.code || row.currency?.code || "";
  // Never fall back to raw id — that flashes in chips/pickers before hydrate.
  const name = row.currency_name || row.name || code || "";
  const countries = Array.isArray(row.countries) ? row.countries : [];
  const countryWithFlag = countries.find((c) => c?.flag_icon || c?.code);
  const flagFromCountry = resolveFlagUrl(countryWithFlag?.flag_icon);
  const flagFromFlags = resolveFlagUrl(
    Array.isArray(row.flags) ? row.flags[0] : ""
  );
  const flagFromCountryCode = flagcdnFromCode(countryWithFlag?.code);
  const icon = flagFromCountry || flagFromFlags || flagFromCountryCode || "";
  return { id: row.id, name, code, icon };
}

/**
 * Page state for Fair Pay → Pay Ranges List (ER-10459 / ER-10690).
 */
const VERSION_REJECTION_MESSAGES = {
  PAY_RANGE_VERSION_DRAFT_EXISTS:
    "This pay range already has a draft version. The list has been refreshed.",
  PAY_RANGE_VERSION_FUTURE_EXISTS:
    "This pay range already has a next version. Void it first. The list has been refreshed.",
};

const VERSION_DIALOG_CLOSED = {
  show: false,
  kind: "",
  payRangeId: null,
  versionId: null,
  name: "",
  versionLabel: "",
};

/** @param {{ emit?: Function }} [options] emit: the page's emit, used for `new-version` / `edit-draft`. */
export function usePayRangesListPageState({ emit } = {}) {
  const filter = useFilters();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  const DEFAULT_STATUS_FILTER = getDefaultFiltersForKey("payRangeList") || [
    "draft",
    "active",
  ];

  /** token → chip label (country:{id} / currency:{id}) */
  const countryTokenToLabel = new Map();
  const currencyTokenToLabel = new Map();
  /** Bumped when label maps change so chipText recomputes after hydrate / tab remount. */
  const pickerLabelRevision = ref(0);

  function bumpPickerLabels() {
    pickerLabelRevision.value += 1;
  }

  const state = reactive({
    rows: [],
    loading: false,
    /** True when the last list request failed — the table shows the error state + Retry. */
    loadError: false,
    showReferenceCurrency: false,
    page: 1,
    pageSize: PAGE_SIZE,
    count: 0,
    /** Bumps after create/lifecycle so DxTreeList remounts with fresh data. */
    tableKey: 0,
    /** Rule 18 "Show version as of" — a version selector, never a row filter. Gated by
     *  idea-MDP-429; `custom` false means "Today" (resolved fresh on every fetch). */
    asOf: {
      enabled: false,
      custom: false,
      date: "",
      pickerOpen: false,
    },
    filters: {
      key: "payRangeList",
      values: [...DEFAULT_STATUS_FILTER],
      searchText: "",
      showFilterDialog: false,
      clearSelection: "",
      selectedCountry: null,
      selectedCurrency: null,
    },
    createModal: {
      show: false,
      mode: "create",
      payRangeId: null,
      stepNumber: 1,
      initialValues: {},
    },
    retireDialog: {
      show: false,
      id: null,
      name: "",
      retireOn: "",
      minRetireOn: "",
    },
    deleteDialog: {
      show: false,
      id: null,
      name: "",
    },
    /** Void next version / Publish draft / Discard draft confirmation (Rule 33, ER-12162). */
    versionDialog: { ...VERSION_DIALOG_CLOSED },
    chatter: {
      show: false,
      listData: {},
      typeName: "Pay Range",
      typeValue: "",
      iconName: "popup_pay_range",
      items: [],
      total: 0,
    },
  });

  /** Local calendar date (YYYY-MM-DD) — never converted through UTC. */
  function todayLocalIso() {
    return toApiDateString(new Date());
  }

  /** ISO date the list is currently resolved against (shown in the control, sent as `as_of`). */
  const asOfDate = computed(() =>
    state.asOf.custom && state.asOf.date ? state.asOf.date : todayLocalIso()
  );
  const asOfIsToday = computed(() => !state.asOf.custom);

  const pageInfo = computed(() => ({
    count: state.count,
    page: state.page,
    pageSize: state.pageSize,
  }));

  /** "Showing 1–5 of 243 pay ranges" — empty when there is nothing to count. */
  const resultCountLabel = computed(() => {
    const { count, page, pageSize } = pageInfo.value;
    if (!count || state.loadError) return "";
    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, count);
    return `Showing ${first}–${last} of ${count} pay ranges`;
  });

  function resolveFilterToken(raw) {
    if (raw == null || raw === "") return null;
    const str = String(raw).trim();
    if (!str || str === "No Filter" || str === "no_filter") return null;

    const lower = str.toLowerCase();
    if (STATUS_SET.has(lower)) return lower;

    const statusFromChip = Object.entries(STATUS_CHIP).find(
      ([, label]) => label === str || label.toLowerCase() === lower
    );
    if (statusFromChip) return statusFromChip[0];

    const payByLabel = PAY_BASIS_OPTIONS.find((o) => o.label === str);
    if (payByLabel) return payByLabel.value;
    if (PAY_BASIS_VALUES.has(str)) return str;

    const workByLabel = WORK_MODE_OPTIONS.find((o) => o.label === str);
    if (workByLabel) return workByLabel.value;
    if (WORK_MODE_VALUES.has(str)) return str;

    if (str.startsWith(COUNTRY_PREFIX) || str.startsWith(CURRENCY_PREFIX)) {
      return str;
    }

    // SearchBar emits chip display labels; reverse-map country/currency names → tokens.
    for (const [token, label] of countryTokenToLabel.entries()) {
      if (label === str) return token;
    }
    for (const [token, label] of currencyTokenToLabel.entries()) {
      if (label === str) return token;
    }

    const selectedCountry = state.filters.selectedCountry;
    if (selectedCountry?.id != null) {
      const countryLabel = selectedCountry.name || String(selectedCountry.id);
      if (countryLabel === str || String(selectedCountry.id) === str) {
        return `${COUNTRY_PREFIX}${selectedCountry.id}`;
      }
    }

    const selectedCurrency = state.filters.selectedCurrency;
    if (selectedCurrency?.id != null) {
      const currencyLabel =
        selectedCurrency.code &&
        selectedCurrency.name &&
        selectedCurrency.name !== selectedCurrency.code
          ? `${selectedCurrency.name} (${selectedCurrency.code})`
          : selectedCurrency.code ||
            selectedCurrency.name ||
            String(selectedCurrency.id);
      if (
        currencyLabel === str ||
        String(selectedCurrency.id) === str ||
        selectedCurrency.code === str
      ) {
        return `${CURRENCY_PREFIX}${selectedCurrency.id}`;
      }
    }

    // After tab remount, chip may briefly show bare id before hydrate finishes.
    const countryByBareId = (state.filters.values || []).find(
      (v) =>
        String(v).startsWith(COUNTRY_PREFIX) &&
        String(v).slice(COUNTRY_PREFIX.length) === str
    );
    if (countryByBareId) return countryByBareId;

    const currencyByBareId = (state.filters.values || []).find(
      (v) =>
        String(v).startsWith(CURRENCY_PREFIX) &&
        String(v).slice(CURRENCY_PREFIX.length) === str
    );
    if (currencyByBareId) return currencyByBareId;

    return str;
  }

  function chipLabelForValue(value) {
    if (STATUS_SET.has(String(value || "").toLowerCase())) {
      return capitalizeStatus(value);
    }
    const pay = PAY_BASIS_OPTIONS.find((o) => o.value === value);
    if (pay) return pay.label;
    const work = WORK_MODE_OPTIONS.find((o) => o.value === value);
    if (work) return work.label;
    if (countryTokenToLabel.has(value)) return countryTokenToLabel.get(value);
    if (currencyTokenToLabel.has(value)) return currencyTokenToLabel.get(value);

    const countryId = String(value || "").startsWith(COUNTRY_PREFIX)
      ? String(value).slice(COUNTRY_PREFIX.length)
      : null;
    if (
      countryId &&
      state.filters.selectedCountry &&
      String(state.filters.selectedCountry.id) === countryId
    ) {
      const label = countryDisplayName(state.filters.selectedCountry);
      if (label) return label;
    }

    const currencyId = String(value || "").startsWith(CURRENCY_PREFIX)
      ? String(value).slice(CURRENCY_PREFIX.length)
      : null;
    if (
      currencyId &&
      state.filters.selectedCurrency &&
      String(state.filters.selectedCurrency.id) === currencyId
    ) {
      const label = currencyDisplayName(state.filters.selectedCurrency);
      if (label) return label;
    }

    // Prefer stored labels over bare ids (avoids chip flash after remount / apply).
    if (countryId) {
      const stored = readStoredPickerLabels().countries[`${COUNTRY_PREFIX}${countryId}`];
      if (stored) return stored;
    }
    if (currencyId) {
      const stored =
        readStoredPickerLabels().currencies[`${CURRENCY_PREFIX}${currencyId}`];
      if (stored) return stored;
    }

    // Never paint a bare numeric id — keep the prefixed token out of the chip row.
    if (countryId || currencyId) return "";
    return value;
  }

  const chipText = computed(() => {
    // Depend on revision so labels refresh after Map hydrate (tab remount).
    void pickerLabelRevision.value;
    return (state.filters.values || []).map((v) => chipLabelForValue(v));
  });

  const filterLabel = computed(() =>
    (state.filters.values || []).length > 0 ? "More filters" : "Filters"
  );

  const hasFilterValue = (target) =>
    (state.filters.values || []).some(
      (f) => String(f).toLowerCase() === String(target).toLowerCase()
    );

  const filterModalInitialStatuses = computed(() => ({
    draft: hasFilterValue("draft"),
    active: hasFilterValue("active"),
    retired: hasFilterValue("retired"),
  }));

  const filterModalInitialIncludeArchived = computed(() =>
    hasFilterValue("archived")
  );

  const filterModalInitialPayBasis = computed(() => {
    const split = splitFilters(state.filters.values);
    return split.payBasis;
  });

  const filterModalInitialWorkMode = computed(() => {
    const split = splitFilters(state.filters.values);
    return split.workMode;
  });

  const filterModalInitialCurrency = computed(
    () => state.filters.selectedCurrency
  );

  const filterModalInitialCountry = computed(
    () => state.filters.selectedCountry
  );

  const hasSearchApplied = computed(() => !!state.filters.searchText?.trim());

  const hasNonDefaultFiltersApplied = computed(
    () => !sameFilterSet(state.filters.values, DEFAULT_STATUS_FILTER)
  );

  const hasActiveSearchOrFilters = computed(
    () => hasSearchApplied.value || hasNonDefaultFiltersApplied.value
  );

  const clearFilterLabel = computed(() => {
    if (hasSearchApplied.value && hasNonDefaultFiltersApplied.value) {
      return "Clear All";
    }
    if (hasSearchApplied.value) return "Clear Search";
    if (hasNonDefaultFiltersApplied.value) return "Clear Filter";
    return "Clear Filter";
  });

  function notifySuccess(message) {
    if (proxy?.$notifications?.success) {
      proxy.$notifications.success(message);
    }
  }

  function notifyError(err, fallback = "Something went wrong") {
    const message =
      err?.response?.data?.error ||
      err?.response?.data?.detail ||
      err?.message ||
      fallback;
    if (proxy?.$notifications?.error) {
      proxy.$notifications.error(message);
    }
  }

  function splitFilters(values) {
    const statuses = [];
    let payBasis = null;
    let workMode = null;
    let operatingCountry = null;
    let operatingCurrency = null;

    (values || []).forEach((raw) => {
      const value = resolveFilterToken(raw);
      if (!value) return;
      const lower = String(value).toLowerCase();
      if (STATUS_SET.has(lower)) {
        statuses.push(lower);
        return;
      }
      if (PAY_BASIS_VALUES.has(value)) {
        payBasis = value;
        return;
      }
      if (WORK_MODE_VALUES.has(value)) {
        workMode = value;
        return;
      }
      if (String(value).startsWith(COUNTRY_PREFIX)) {
        operatingCountry = String(value).slice(COUNTRY_PREFIX.length);
        return;
      }
      if (String(value).startsWith(CURRENCY_PREFIX)) {
        operatingCurrency = String(value).slice(CURRENCY_PREFIX.length);
      }
    });

    return { statuses, payBasis, workMode, operatingCountry, operatingCurrency };
  }

  function rememberCountry(item) {
    const normalized = normalizeCountrySelection(item);
    if (!normalized?.id) return;
    const token = `${COUNTRY_PREFIX}${normalized.id}`;
    const label = countryDisplayName(normalized);
    if (label) {
      countryTokenToLabel.set(token, label);
      writeStoredPickerLabels(countryTokenToLabel, currencyTokenToLabel);
      bumpPickerLabels();
    }
  }

  function rememberCurrency(item) {
    const normalized = normalizeCurrencySelection(item);
    if (!normalized?.id) return;
    const token = `${CURRENCY_PREFIX}${normalized.id}`;
    const label = currencyDisplayName(normalized);
    if (label) {
      currencyTokenToLabel.set(token, label);
      writeStoredPickerLabels(countryTokenToLabel, currencyTokenToLabel);
      bumpPickerLabels();
    }
  }

  /**
   * Hydrate selected country/currency chip objects from stored filter tokens.
   * Pickers in the filter modal load their own scroll-paginated lists.
   */
  async function hydrateSelectedPickersFromTokens(values) {
    const split = splitFilters(values);
    const accountId = getAccountId();
    if (!accountId) return;

    const tasks = [];

    if (split.operatingCountry && !state.filters.selectedCountry) {
      tasks.push(
        (async () => {
          try {
            const token = `${COUNTRY_PREFIX}${split.operatingCountry}`;
            const storedLabel =
              countryTokenToLabel.get(token) ||
              readStoredPickerLabels().countries[token];
            const response = await getOperatingCountry(accountId, {
              offset: 0,
              size: PICKER_PAGE_SIZE,
              search: storedLabel || undefined,
            });
            const match =
              extractResults(response).find(
                (row) => String(row?.id) === String(split.operatingCountry)
              ) || null;
            const item = mapCountryPickerItem(match);
            if (item) {
              rememberCountry(item);
              state.filters.selectedCountry = normalizeCountrySelection(item);
            } else if (storedLabel) {
              state.filters.selectedCountry = {
                id: Number(split.operatingCountry) || split.operatingCountry,
                name: storedLabel,
                icon: "",
              };
              rememberCountry(state.filters.selectedCountry);
            }
          } catch {
            /* keep empty selection */
          }
        })()
      );
    }

    if (split.operatingCurrency && !state.filters.selectedCurrency) {
      tasks.push(
        (async () => {
          try {
            const token = `${CURRENCY_PREFIX}${split.operatingCurrency}`;
            const storedLabel =
              currencyTokenToLabel.get(token) ||
              readStoredPickerLabels().currencies[token];
            const response = await getOperatingCurrency(accountId, {
              offset: 0,
              size: PICKER_PAGE_SIZE,
              search: storedLabel || undefined,
            });
            const match =
              extractResults(response).find(
                (row) => String(row?.id) === String(split.operatingCurrency)
              ) || null;
            const item = mapCurrencyPickerItem(match);
            if (item) {
              rememberCurrency(item);
              state.filters.selectedCurrency = normalizeCurrencySelection(item);
            } else if (storedLabel) {
              state.filters.selectedCurrency = {
                id: Number(split.operatingCurrency) || split.operatingCurrency,
                name: storedLabel,
                code: "",
                icon: "",
              };
              rememberCurrency(state.filters.selectedCurrency);
            }
          } catch {
            /* keep empty selection */
          }
        })()
      );
    }

    if (tasks.length) {
      await Promise.all(tasks);
      bumpPickerLabels();
    }
  }

  async function fetchRows() {
    state.loading = true;
    state.loadError = false;
    try {
      const split = splitFilters(state.filters.values);
      const params = {
        page: state.page,
        pageSize: state.pageSize,
        // Rule 18 (ER-12098): the list search sends the exact `code` filter — the
        // backend dropped the `name` substring filter in ER-11730 and only reads
        // `code` (exact) and `job_family_level` (exact id) on this list.
        code: state.filters.searchText?.trim() || undefined,
        pay_basis: split.payBasis || undefined,
        work_mode: split.workMode || undefined,
        operating_country: split.operatingCountry || undefined,
        operating_currency: split.operatingCurrency || undefined,
      };
      if (state.asOf.enabled) {
        params.as_of = asOfDate.value;
      }
      // When no status chips are set, omit status so the API hides ARCHIVED (Rule 18).
      if (split.statuses.length > 0) {
        params.status = mapUiStatusesToApi(split.statuses);
      }

      const response = await listPayRanges(params);
      const data = response?.data || {};
      const results = Array.isArray(data.results)
        ? data.results
        : Array.isArray(data)
          ? data
          : [];
      const asOfPast = state.asOf.enabled && state.asOf.custom && asOfDate.value < todayLocalIso();
      state.rows = results.map((row) =>
        mapPayRangeListRow(row, {
          asOfActive: state.asOf.enabled,
          versionHistory: state.asOf.enabled,
          asOfPast,
        })
      );
      state.count = data.count ?? results.length;
      state.tableKey += 1;
    } catch (err) {
      state.rows = [];
      state.count = 0;
      state.loadError = true;
      notifyError(err, "Failed to load pay ranges");
    } finally {
      state.loading = false;
    }
  }

  async function loadAsOfFlag() {
    try {
      state.asOf.enabled = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
    } catch {
      state.asOf.enabled = false;
    }
  }

  function openAsOfPicker() {
    state.asOf.pickerOpen = true;
  }

  function closeAsOfPicker() {
    state.asOf.pickerOpen = false;
  }

  async function applyAsOfDate(date) {
    closeAsOfPicker();
    const iso = toApiDateString(date);
    if (!iso) return;
    state.asOf.custom = iso !== todayLocalIso();
    state.asOf.date = state.asOf.custom ? iso : "";
    state.page = 1;
    await fetchRows();
  }

  async function resetAsOfToToday() {
    state.asOf.custom = false;
    state.asOf.date = "";
    state.page = 1;
    await fetchRows();
  }

  async function loadFiltersOnReload() {
    // Restore chip labels before values paint so SearchBar never flashes bare ids.
    const stored = readStoredPickerLabels();
    Object.entries(stored.countries).forEach(([token, label]) => {
      if (token && label) countryTokenToLabel.set(token, label);
    });
    Object.entries(stored.currencies).forEach(([token, label]) => {
      if (token && label) currencyTokenToLabel.set(token, label);
    });
    if (
      Object.keys(stored.countries).length ||
      Object.keys(stored.currencies).length
    ) {
      bumpPickerLabels();
    }

    const saved = filter.getFilters(state.filters.key);
    if (saved && saved.length > 0) {
      state.filters.values = saved
        .map((v) => resolveFilterToken(v) || v)
        .filter(Boolean);
    } else {
      state.filters.values = [...DEFAULT_STATUS_FILTER];
    }
    await hydrateSelectedPickersFromTokens(state.filters.values);
  }

  function persistFilters(next) {
    filter.setFilters({ key: state.filters.key, filters: next });
    state.filters.values = next;
  }

  /**
   * Apply payload from PayRangeTableFilterModal → filter tokens + list fetch.
   * archived is unioned into the status multi-value list (ER-10690).
   */
  async function applyFilterModal(payload) {
    const next = [...(payload?.status || [])];
    if (payload?.includeArchived) next.push("archived");

    if (payload?.payBasis && PAY_BASIS_VALUES.has(payload.payBasis)) {
      next.push(payload.payBasis);
    }
    if (payload?.workMode && WORK_MODE_VALUES.has(payload.workMode)) {
      next.push(payload.workMode);
    }

    if (payload?.country?.id) {
      const country = normalizeCountrySelection(payload.country);
      rememberCountry(country);
      state.filters.selectedCountry = country;
      next.push(`${COUNTRY_PREFIX}${country.id}`);
    } else {
      state.filters.selectedCountry = null;
    }

    if (payload?.currency?.id) {
      const currency = normalizeCurrencySelection(payload.currency);
      rememberCurrency(currency);
      state.filters.selectedCurrency = currency;
      next.push(`${CURRENCY_PREFIX}${currency.id}`);
    } else {
      state.filters.selectedCurrency = null;
    }

    persistFilters(next);
    state.page = 1;
    state.filters.showFilterDialog = false;
    await fetchRows();
  }

  async function removeFilter(removed) {
    const token = resolveFilterToken(removed);
    const next = (state.filters.values || []).filter(
      (f) => String(f) !== String(token) && String(f) !== String(removed)
    );

    if (String(token || "").startsWith(COUNTRY_PREFIX)) {
      state.filters.selectedCountry = null;
      countryTokenToLabel.delete(token);
      bumpPickerLabels();
    }
    if (String(token || "").startsWith(CURRENCY_PREFIX)) {
      state.filters.selectedCurrency = null;
      currencyTokenToLabel.delete(token);
      bumpPickerLabels();
    }

    state.filters.clearSelection = STATUS_SET.has(String(token || "").toLowerCase())
      ? String(token).toLowerCase()
      : chipLabelForValue(token) || removed;
    persistFilters(next);
    state.page = 1;
    await fetchRows();
  }

  async function clearAllFilters() {
    persistFilters([...DEFAULT_STATUS_FILTER]);
    state.filters.searchText = "";
    state.filters.clearSelection = "clear-all";
    state.filters.selectedCountry = null;
    state.filters.selectedCurrency = null;
    state.page = 1;
    await fetchRows();
  }

  async function performSearch(value) {
    state.filters.searchText = value == null ? "" : value;
    state.page = 1;
    await fetchRows();
  }

  const onSearch = debounce(performSearch, SEARCH_DEBOUNCE_MS);

  async function onPageChange({ page }) {
    state.page = page || 1;
    await fetchRows();
  }

  function openCreateModal() {
    state.createModal = {
      show: true,
      mode: "create",
      payRangeId: null,
      stepNumber: 1,
      initialValues: {},
    };
  }

  function closeCreateModal() {
    state.createModal.show = false;
    state.createModal.payRangeId = null;
    state.createModal.initialValues = {};
    state.createModal.stepNumber = 1;
    state.createModal.mode = "create";
  }

  function navigateToDetail(id) {
    if (!id || !proxy?.$router) return;
    proxy.$router
      .push({ name: "FairPayPayRangeDetail", params: { payRangeId: String(id) } })
      .catch(() => {});
  }

  function onOpenDetail({ id } = {}) {
    navigateToDetail(id);
  }

  function onOpenPositions({ query } = {}) {
    if (!query || !proxy?.$router) return;
    proxy.$router.push(fairPayListRoute(FAIR_PAY_TAB.POSITION_PAY_RANGES, query)).catch(() => {});
  }

  async function handleSaveDraft(form) {
    try {
      const response = await savePayRangeDraft(form);
      notifySuccess(form.mode === "edit" ? "Draft updated" : "Draft created");
      // Ensure Draft is in the status filter so the new row is visible after refresh.
      const values = state.filters.values || [];
      if (!values.some((v) => String(v).toLowerCase() === "draft")) {
        persistFilters(["draft", ...values]);
      }
      state.page = 1;
      state.filters.searchText = "";
      closeCreateModal();
      const newId = response?.data?.id;
      // ER-10758: after create-draft, open the detail page.
      if (form.mode !== "edit" && newId) {
        navigateToDetail(newId);
        return response;
      }
      await fetchRows();
      return response;
    } catch (err) {
      notifyError(
        err,
        form.mode === "edit" ? "Failed to update draft" : "Failed to create draft"
      );
      throw err;
    }
  }

  function handleUploadDocument(file, options) {
    return uploadPayRangeSourceDocument(file, options);
  }

  function openRetire(row) {
    const today = toApiDateString(new Date());
    const minRetireOn = dayAfterApiDate(row.version_date) || "";
    state.retireDialog = {
      show: true,
      id: row.id,
      name: row.name || "",
      retireOn: maxApiDate(today, minRetireOn) || today || "",
      minRetireOn,
    };
  }

  function closeRetire() {
    state.retireDialog.show = false;
    state.retireDialog.id = null;
    state.retireDialog.name = "";
    state.retireDialog.retireOn = "";
    state.retireDialog.minRetireOn = "";
  }

  async function confirmRetire() {
    const retireOn = toApiDateString(state.retireDialog.retireOn);
    const minRetireOn = toApiDateString(state.retireDialog.minRetireOn);
    if (!state.retireDialog.id || !retireOn) {
      closeRetire();
      return;
    }
    if (minRetireOn && retireOn < minRetireOn) {
      return;
    }
    try {
      await payRangeLifecycleAction(state.retireDialog.id, "retire", {
        retire_on: retireOn,
      });
      notifySuccess("Pay range retired");
      closeRetire();
      await fetchRows();
    } catch (err) {
      notifyError(err, "Failed to retire pay range");
    }
  }

  async function runLifecycle(id, action, successMessage, method = "post", body = {}) {
    try {
      await payRangeLifecycleAction(id, action, body, method);
      notifySuccess(successMessage);
      await fetchRows();
    } catch (err) {
      notifyError(err, `Failed to ${action.replace(/-/g, " ")}`);
    }
  }

  function openDeleteDraft(row) {
    state.deleteDialog = {
      show: true,
      id: row.id,
      name: row.name || "",
    };
  }

  function closeDeleteDraft() {
    state.deleteDialog.show = false;
    state.deleteDialog.id = null;
    state.deleteDialog.name = "";
  }

  async function confirmDeleteDraft() {
    if (!state.deleteDialog.id) {
      closeDeleteDraft();
      return;
    }
    const id = state.deleteDialog.id;
    closeDeleteDraft();
    await runLifecycle(id, "delete-draft", "Draft deleted", "delete");
  }

  function openVersionDialog(kind, row, version) {
    state.versionDialog = {
      show: true,
      kind,
      payRangeId: row.id,
      versionId: version?.id ?? null,
      name: row.name || "",
      versionLabel: version?.version_number != null ? `V${version.version_number}` : "",
    };
  }

  function closeVersionDialog() {
    state.versionDialog = { ...VERSION_DIALOG_CLOSED };
  }

  async function confirmVersionDialog() {
    const { kind, payRangeId, versionId } = state.versionDialog;
    closeVersionDialog();
    if (!payRangeId || !versionId) return;
    try {
      if (kind === "void") {
        await voidPayRangeVersion(payRangeId, versionId, {});
        notifySuccess("Next version voided");
      } else if (kind === "publish") {
        await publishPayRangeVersion(payRangeId, versionId);
        notifySuccess("Draft published");
      } else if (kind === "discard") {
        await discardPayRangeVersion(payRangeId, versionId);
        notifySuccess("Draft discarded");
      }
      await fetchRows();
    } catch (err) {
      notifyError(err, `Failed to ${kind} version`);
      await fetchRows();
    }
  }

  /**
   * Create-version rejected because the list was stale (a Draft or a Future version already
   * exists): toast the reason and refetch. Returns true when the error was one of those.
   */
  async function handleVersionRejection(err) {
    const code = err?.response?.data?.error;
    const message = VERSION_REJECTION_MESSAGES[code];
    if (!message) return false;
    if (proxy?.$notifications?.error) {
      proxy.$notifications.error(message);
    }
    await fetchRows();
    return true;
  }

  async function onRowAction({ id, action }) {
    const row = state.rows.find((r) => String(r.id) === String(id));
    switch (action) {
      case "retire":
        if (row) openRetire(row);
        break;
      case "reactivate":
        await runLifecycle(id, "reactivate", "Pay range reactivated");
        break;
      case "archive":
        await runLifecycle(id, "archive", "Pay range archived");
        break;
      case "unarchive":
        await runLifecycle(id, "unarchive", "Pay range unarchived");
        break;
      case "delete-draft":
        if (row) openDeleteDraft(row);
        break;
      case "new-version":
        if (row) emit?.("new-version", { id: row.id, code: row.code, name: row.name });
        break;
      case "edit-draft":
        if (row?.pending_draft) {
          emit?.("edit-draft", {
            id: row.id,
            code: row.code,
            name: row.name,
            versionId: row.pending_draft.id,
          });
        }
        break;
      case "void-next-version":
        if (row?.future_version) openVersionDialog("void", row, row.future_version);
        break;
      case "publish-draft":
        if (row?.pending_draft) openVersionDialog("publish", row, row.pending_draft);
        break;
      case "discard-draft":
        if (row?.pending_draft) openVersionDialog("discard", row, row.pending_draft);
        break;
      default:
        break;
    }
  }

  function onOpenChatter({ id, name, code } = {}) {
    const rowId = id ?? null;
    if (rowId == null) return;

    const row =
      state.rows.find((r) => String(r.id) === String(rowId)) || null;
    const displayName = name || row?.name || code || row?.code || "Pay Range";

    state.chatter.show = true;
    state.chatter.listData = {
      id: rowId,
      code: code || row?.code || "",
      name: displayName,
      status: row?.status,
    };
    state.chatter.typeValue = displayName;
    state.chatter.typeName = "Pay Range";
    state.chatter.iconName = "popup_pay_range";
    state.chatter.items = [];
    state.chatter.total = 0;

    loadChatterData(rowId, 1).catch((err) => {
      notifyError(err, "Failed to load pay range chatter");
    });
  }

  async function loadChatterData(recordId, page = 1) {
    const accountId = localStorage.getItem("currentAccountID");
    if (!accountId || recordId == null) return;

    const { data } = await getChatterList(
      accountId,
      recordId,
      PAY_RANGE_CHATTER_TABLE,
      page
    );

    if (data?.count != null) {
      state.chatter.total = data.count;
    }
    if (data?.results?.length) {
      state.chatter.items.push(
        ...transformPayRangeChatterResults(data.results)
      );
    }
  }

  function closeChatterDrawer() {
    state.chatter.show = false;
  }

  async function loadMoreChatter(recordData) {
    const recordId = recordData?.id;
    if (recordId == null) return;

    try {
      const page = Math.ceil(state.chatter.items.length / 10) + 1;
      await loadChatterData(recordId, page);
    } catch (err) {
      notifyError(err, "Failed to load more pay range chatter");
    }
  }

  return {
    state,
    pageInfo,
    resultCountLabel,
    chipText,
    filterLabel,
    filterModalInitialStatuses,
    filterModalInitialIncludeArchived,
    filterModalInitialPayBasis,
    filterModalInitialWorkMode,
    filterModalInitialCurrency,
    filterModalInitialCountry,
    hasActiveSearchOrFilters,
    clearFilterLabel,
    fetchRows,
    asOfDate,
    asOfIsToday,
    loadAsOfFlag,
    openAsOfPicker,
    closeAsOfPicker,
    applyAsOfDate,
    resetAsOfToToday,
    loadFiltersOnReload,
    applyFilterModal,
    removeFilter,
    clearAllFilters,
    onSearch,
    onPageChange,
    openCreateModal,
    closeCreateModal,
    handleSaveDraft,
    handleUploadDocument,
    onRowAction,
    onOpenChatter,
    onOpenDetail,
    onOpenPositions,
    closeChatterDrawer,
    loadMoreChatter,
    closeRetire,
    confirmRetire,
    closeDeleteDraft,
    confirmDeleteDraft,
    closeVersionDialog,
    confirmVersionDialog,
    handleVersionRejection,
  };
}
